import { gql, type TypedDocumentNode } from "@apollo/client";

export type CallTopicSubtopicStat = {
  subtopic: string;
  count: number;
  avgDurationSeconds: number | null;
  totalDurationSeconds: number | null;
};

export type CallTopicStat = {
  topic: string;
  count: number;
  avgDurationSeconds: number | null;
  totalDurationSeconds: number | null;
  subtopics: CallTopicSubtopicStat[];
};

export type CallTopicDayCount = {
  topic: string;
  count: number;
};

export type CallTopicDay = {
  date: string;
  total: number;
  inbound: number;
  outbound: number;
  topics: CallTopicDayCount[];
};

export type CallTopicRecentCall = {
  callSid: string;
  startedAt: string;
  direction: string | null;
  durationSeconds: number | null;
  topic: string;
  subtopic: string | null;
  interactionId: string | null;
  personPgaId: string | null;
  workerName: string | null;
  sentimentLabel: string | null;
  recordingUrl: string | null;
};

export type CallTopicWorkerStat = {
  workerName: string | null;
  calls: number;
  inboundCalls: number;
  outboundCalls: number;
  avgDurationSeconds: number | null;
  totalDurationSeconds: number;
  analyzedCalls: number;
  topTopic: string | null;
  positiveCalls: number;
  neutralCalls: number;
  negativeCalls: number;
  mixedCalls: number;
};

export type CallTopicStats = {
  totalCalls: number;
  analyzedCalls: number;
  pendingCalls: number;
  failedCalls: number;
  avgDurationSeconds: number | null;
  topics: CallTopicStat[];
  days: CallTopicDay[];
  workers: CallTopicWorkerStat[];
  recentCalls: CallTopicRecentCall[];
};

export type CallTopicStatsResponse = {
  callTopicStats: CallTopicStats | null;
};

export type CallTopicStatsVariables = {
  startDateTime: string;
  endDateTime: string;
  direction?: string | null;
  workerName?: string | null;
};

export type CallTopicHighlights = {
  bullets: string[];
  generatedAt: string | null;
};

export type CallTopicHighlightsResponse = {
  callTopicHighlights: CallTopicHighlights | null;
};

export type CallTopicHighlightsVariables = {
  startDateTime: string;
  endDateTime: string;
  direction?: string | null;
  topics?: string[] | null;
  workerName?: string | null;
};

export type FlexCallDetails = {
  callSid: string;
  transcriptSummary: string | null;
  transcriptText: string | null;
  recordingSid: string | null;
};

export type FlexCallDetailsResponse = {
  twilioFlexCall: FlexCallDetails | null;
};

export type FlexCallDetailsVariables = {
  callSid: string;
};

export const FLEX_CALL_DETAILS_QUERY: TypedDocumentNode<FlexCallDetailsResponse, FlexCallDetailsVariables> = gql`
  query FlexCallDetails($callSid: String!) {
    twilioFlexCall(callSid: $callSid) {
      callSid
      transcriptSummary
      transcriptText
      recordingSid
    }
  }
`;

export const CALL_TOPIC_HIGHLIGHTS_QUERY: TypedDocumentNode<CallTopicHighlightsResponse, CallTopicHighlightsVariables> =
  gql`
    query CallTopicHighlights(
      $startDateTime: ISO8601DateTime!
      $endDateTime: ISO8601DateTime!
      $direction: String
      $topics: [String!]
      $workerName: String
    ) {
      callTopicHighlights(
        startDateTime: $startDateTime
        endDateTime: $endDateTime
        direction: $direction
        topics: $topics
        workerName: $workerName
      ) {
        bullets
        generatedAt
      }
    }
  `;

export const CALL_TOPIC_STATS_QUERY: TypedDocumentNode<CallTopicStatsResponse, CallTopicStatsVariables> = gql`
  query CallTopicStats(
    $startDateTime: ISO8601DateTime!
    $endDateTime: ISO8601DateTime!
    $direction: String
    $workerName: String
  ) {
    callTopicStats(
      startDateTime: $startDateTime
      endDateTime: $endDateTime
      direction: $direction
      workerName: $workerName
    ) {
      totalCalls
      analyzedCalls
      pendingCalls
      failedCalls
      avgDurationSeconds
      topics {
        topic
        count
        avgDurationSeconds
        totalDurationSeconds
        subtopics {
          subtopic
          count
          avgDurationSeconds
          totalDurationSeconds
        }
      }
      days {
        date
        total
        inbound
        outbound
        topics {
          topic
          count
        }
      }
      recentCalls {
        callSid
        startedAt
        direction
        durationSeconds
        topic
        subtopic
        interactionId
        personPgaId
        workerName
        sentimentLabel
        recordingUrl
      }
      workers {
        workerName
        calls
        inboundCalls
        outboundCalls
        avgDurationSeconds
        totalDurationSeconds
        analyzedCalls
        topTopic
        positiveCalls
        neutralCalls
        negativeCalls
        mixedCalls
      }
    }
  }
`;
